import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { getExportFileName } from "../../utils/fileExportNaming";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  MoreVertical,
  Power,
  Edit,
  Eye,
  Download,
  Upload,
  X,
  History,
} from "lucide-react";
import {
  getAllVehicles,
  getVehicleById,
  updateVehicle,
  assignVehicleToClient,
  getAllVehicleClientAssignments,
  deleteVehicleClientAssignment,
  uploadVehicleSheet,
  getAccountDetails,
} from "../../api/vehicalsManagementApi";
import { getAllClients } from "../../api/createClientApi";
import { getAllDrivers } from "../../api/driverApi";
import AddVehicleModal from "./AddVehicleModal";
import VehicleDetailsModal from "./VehicleDetailsModal";
import VehicleHistoryModal from "./VehicleHistoryModal";
import CustomSelect from "../shared/CustomSelect";
import { toast } from "../shared/Toast";
import { VEHICLE_TYPES, getVehicleTypeBadgeStyle } from "../../constants/vehicleConstants";
import { showConfirm } from "../shared/ConfirmContext";

const StatCard = ({ value, label, isActive, onClick }) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: isActive ? "#E8F5EE" : "#FFFFFF",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      flex: 1,
      padding: "24px 16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: isActive ? "2px solid #07A95B" : "2px solid transparent",
      transition: "all 0.2s ease",
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = "#F5FAF7";
        e.currentTarget.style.border = "2px solid #D1E7DD";
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = "#FFFFFF";
        e.currentTarget.style.border = "2px solid transparent";
      }
    }}
  >
    <div
      style={{
        fontSize: "56px",
        fontWeight: 700,
        color: "#07A95B",
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: "18px",
        fontWeight: 600,
        color: "#333333",
        marginTop: "4px",
      }}
    >
      {label}
    </div>
  </div>
);

const FilterDropdown = ({ isOpen, onClose, clientsList, onApplyFilters }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState("");

  if (!isOpen) return null;

  const handleApply = () => {
    onApplyFilters({
      clientName: selectedClient,
      vehicleType: selectedVehicleType,
    });
    onClose();
  };

  const handleReset = () => {
    setSelectedClient("");
    setSelectedVehicleType("");
    onApplyFilters({
      clientName: "",
      vehicleType: "",
    });
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingRight: "24px",
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          animation: "fadeIn 0.2s ease-out",
        }}
      />
      <div
        style={{
          position: "relative",
          width: "360px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          border: "none",
          zIndex: 101,
          animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              color: "#1F2937",
            }}
          >
            Filters
          </h3>
          <div
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#F3F4F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#E5E7EB")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#F3F4F6")
            }
          >
            <span style={{ fontSize: "18px", color: "#6B7280", lineHeight: 1 }}>
              ×
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Client Name */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <label
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                letterSpacing: "0.025em",
              }}
            >
              Client Name
            </label>
            <CustomSelect
              value={selectedClient}
              onChange={(val) => setSelectedClient(val)}
              options={[
                { value: "", label: "All Clients" },
                ...clientsList.map((c) => ({
                  value: c.companyName || c.name,
                  label: c.companyName || c.name,
                })),
              ]}
              placeholder="All Clients"
              searchable
              width="100%"
            />
          </div>

          {/* Vehicle Type */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <label
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                letterSpacing: "0.025em",
              }}
            >
              Vehicle Type
            </label>
            <CustomSelect
              value={selectedVehicleType}
              onChange={(val) => setSelectedVehicleType(val)}
              options={[
                { value: "", label: "All Types" },
                ...VEHICLE_TYPES.map((type) => ({ value: type, label: type })),
              ]}
              placeholder="All Types"
              width="100%"
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          <button
            onClick={handleReset}
            style={{
              backgroundColor: "white",
              color: "#6B7280",
              border: "2px solid #E5E7EB",
              borderRadius: "8px",
              padding: "14px 24px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              flex: 1,
              transition: "all 0.2s",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F9FAFB";
              e.currentTarget.style.borderColor = "#D1D5DB";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#E5E7EB";
            }}
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            style={{
              backgroundColor: "#20B967",
              color: "white",
              border: "2px solid #20B967",
              borderRadius: "8px",
              padding: "14px 24px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              flex: 1,
              transition: "all 0.2s",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#1A8F55";
              e.currentTarget.style.borderColor = "#1A8F55";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#20B967";
              e.currentTarget.style.borderColor = "#20B967";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

const UploadSheetModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.warning("Please select a file");
      return;
    }
    setUploading(true);
    try {
      await onUpload(selectedFile);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          width: "400px",
          maxWidth: "90%",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600 }}>
          Upload Vehicle Sheet
        </h3>
        <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
          Select an Excel or CSV file to upload vehicle data.
        </p>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          style={{ marginBottom: "16px", width: "100%" }}
        />
        {selectedFile && (
          <p
            style={{ margin: "0 0 16px 0", color: "#20B967", fontSize: "13px" }}
          >
            Selected: {selectedFile.name}
          </p>
        )}
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              padding: "10px 16px",
              border: "1px solid #ddd",
              backgroundColor: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              opacity: uploading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            style={{
              padding: "10px 16px",
              border: "none",
              backgroundColor: "#20B967",
              color: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              opacity: uploading || !selectedFile ? 0.6 : 1,
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionBar = ({
  onAddClick,
  onUploadClick,
  vehiclesData,
  selectedVehicles,
  searchValue,
  onSearchChange,
  clientsList,
  onApplyFilters,
}) => {
  const [hoverFilter, setHoverFilter] = useState(false);
  const [hoverDownload, setHoverDownload] = useState(false);
  const [hoverUpload, setHoverUpload] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadButtonRef = useRef(null);
  const [downloadMenuPosition, setDownloadMenuPosition] = useState({
    top: 0,
    right: 0,
  });

  useEffect(() => {
    if (isDownloadOpen && downloadButtonRef.current) {
      const rect = downloadButtonRef.current.getBoundingClientRect();
      setDownloadMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isDownloadOpen]);

  const getDataToDownload = () => {
    if (selectedVehicles && selectedVehicles.length > 0) {
      return selectedVehicles;
    }
    return vehiclesData || [];
  };

  const downloadCSV = () => {
    const dataToDownload = getDataToDownload();
    if (dataToDownload.length === 0) {
      toast.warning("No data to download");
      return;
    }
    const headers = [
      "Driver Name",
      "Vehicle Number",
      "Vehicle Type",
      "Client Name",
      "Status",
    ];
    const rows = dataToDownload.map((v) => [
      v.ownerName || v.driverName || "N/A",
      v.vehicleNumber || v.registrationNumber || "N/A",
      v.vehicleType || v.vehicleBrand || "Sedan",
      v.clientName || "N/A",
      v.status || "active",
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = getExportFileName({ reportName: "vehicles", data: dataToDownload, ext: "csv" });
    a.click();
    URL.revokeObjectURL(url);
    setIsDownloadOpen(false);
  };

  const downloadExcel = () => {
    const dataToDownload = getDataToDownload();
    if (dataToDownload.length === 0) {
      toast.warning("No data to download");
      return;
    }
    const headers = [
      "Driver Name",
      "Vehicle Number",
      "Vehicle Type",
      "Client Name",
      "Status",
    ];
    const rows = dataToDownload.map((v) => [
      v.ownerName || v.driverName || "N/A",
      v.vehicleNumber || v.registrationNumber || "N/A",
      v.vehicleType || v.vehicleBrand || "Sedan",
      v.clientName || "N/A",
      v.status || "active",
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    const fileName = getExportFileName({ reportName: "vehicles", data: dataToDownload, ext: "xlsx" });
    XLSX.writeFile(wb, fileName);
    setIsDownloadOpen(false);
  };

  const downloadPDF = () => {
    const dataToDownload = getDataToDownload();
    if (dataToDownload.length === 0) {
      toast.warning("No data to download");
      return;
    }
    const doc = new jsPDF();
    doc.text("Vehicles List", 14, 15);

    const headers = [
      [
        "Driver Name",
        "Vehicle Number",
        "Vehicle Type",
        "Client Name",
        "Status",
      ],
    ];
    const rows = dataToDownload.map((v) => [
      v.ownerName || v.driverName || "N/A",
      v.vehicleNumber || v.registrationNumber || "N/A",
      v.vehicleType || v.vehicleBrand || "Sedan",
      v.clientName || "N/A",
      v.status || "active",
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 25,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [32, 185, 103], textColor: 255 },
    });

    doc.save("vehicles.pdf");
    setIsDownloadOpen(false);
  };

  return (
    <div
      style={{
        backgroundColor: "#EEF8F3",
        borderRadius: "8px",
        padding: "12px 20px",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "16px",
        marginTop: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#F5F1FA",
          borderRadius: "20px",
          padding: "10px 16px",
          width: "280px",
        }}
      >
        <input
          type="text"
          placeholder="Search vehicle number..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            border: "none",
            background: "transparent",
            outline: "none",
            flexGrow: 1,
            fontFamily: "inherit",
            color: "#333",
            fontSize: "14px",
          }}
        />
        <Search size={18} color="#555" />
      </div>
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          onMouseEnter={() => setHoverFilter(true)}
          onMouseLeave={() => setHoverFilter(false)}
          style={{
            backgroundColor: hoverFilter ? "#1c9d57" : "#20B967",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            transition: "background-color 0.2s",
          }}
        >
          Filters <Filter size={16} />
        </button>
        <FilterDropdown
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          clientsList={clientsList}
          onApplyFilters={onApplyFilters}
        />
      </div>
      <button
        onClick={onUploadClick}
        onMouseEnter={() => setHoverUpload(true)}
        onMouseLeave={() => setHoverUpload(false)}
        style={{
          backgroundColor: hoverUpload ? "#1c9d57" : "#20B967",
          color: "white",
          border: "none",
          borderRadius: "6px",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 500,
          transition: "background-color 0.2s",
        }}
      >
        Upload Sheet <Upload size={16} />
      </button>
      <div style={{ position: "relative" }} ref={downloadButtonRef}>
        <button
          onClick={() => setIsDownloadOpen(!isDownloadOpen)}
          onMouseEnter={() => setHoverDownload(true)}
          onMouseLeave={() => setHoverDownload(false)}
          style={{
            backgroundColor: hoverDownload ? "#f0fcf5" : "white",
            color: "#20B967",
            border: "1px solid #20B967",
            borderRadius: "6px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            transition: "background-color 0.2s",
          }}
        >
          Download <Download size={16} />
        </button>
        {isDownloadOpen && (
          <>
            <div
              onClick={() => setIsDownloadOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 100 }}
            />
            <div
              style={{
                position: "fixed",
                top: downloadMenuPosition.top,
                right: downloadMenuPosition.right,
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                zIndex: 101,
                minWidth: "140px",
                overflow: "hidden",
                border: "1px solid #E5E7EB",
              }}
            >
              <div
                onClick={downloadCSV}
                style={{
                  padding: "11px 16px",
                  fontSize: "14px",
                  color: "#333",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                CSV
              </div>
              <div
                onClick={downloadExcel}
                style={{
                  padding: "11px 16px",
                  fontSize: "14px",
                  color: "#333",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                Excel
              </div>
              <div
                onClick={downloadPDF}
                style={{
                  padding: "11px 16px",
                  fontSize: "14px",
                  color: "#333",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                PDF
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ActionsMenu = ({
  onView,
  onEdit,
  onToggleStatus,
  onAssign,
  onViewHistory,
  hasClient,
  status,
}) => {
  const isInactive = status?.toLowerCase() === "inactive";
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [positionAbove, setPositionAbove] = useState(false);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 220; // Increased for history option
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldPositionAbove =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setPositionAbove(shouldPositionAbove);

      setMenuPosition({
        top: shouldPositionAbove
          ? rect.top + window.scrollY - dropdownHeight
          : rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open]);

  return (
    <div style={{ position: "relative" }} ref={buttonRef}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: "80px",
          display: "flex",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <MoreVertical size={20} color="#555" />
      </div>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 100 }}
          />
          <div
            style={{
              position: "fixed",
              top: menuPosition.top + (positionAbove ? 0 : 4),
              right: menuPosition.right,
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              zIndex: 101,
              minWidth: "160px",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
            }}
          >
            <div
              onClick={() => {
                onView && onView();
                setOpen(false);
              }}
              style={{
                padding: "11px 16px",
                fontSize: "14px",
                color: "#333",
                cursor: "pointer",
                transition: "background-color 0.15s",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F9FAFB")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <Eye size={16} /> View Details
            </div>

            <div
              onClick={() => {
                onViewHistory && onViewHistory();
                setOpen(false);
              }}
              style={{
                padding: "11px 16px",
                fontSize: "14px",
                color: "#333",
                cursor: "pointer",
                transition: "background-color 0.15s",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F9FAFB")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <History size={16} /> View History
            </div>
            <div
              onClick={() => {
                onEdit && onEdit();
                setOpen(false);
              }}
              style={{
                padding: "11px 16px",
                fontSize: "14px",
                color: "#333",
                cursor: "pointer",
                transition: "background-color 0.15s",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F9FAFB")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <Edit size={16} /> Edit
            </div>
            <div
              onClick={() => {
                onToggleStatus && onToggleStatus();
                setOpen(false);
              }}
              style={{
                padding: "11px 16px",
                fontSize: "14px",
                color: isInactive ? "#227B48" : "#F59E0B",
                cursor: "pointer",
                transition: "background-color 0.15s",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F9FAFB")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <Power size={16} /> {isInactive ? "Activate" : "Deactivate"}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TableHeader = ({ allSelected, onSelectAll }) => (
  <div
    style={{
      display: "flex",
      backgroundColor: "#212A30",
      color: "white",
      padding: "12px 16px",
      fontSize: "13px",
      fontWeight: 500,
    }}
  >
    <div style={{ width: "50px", display: "flex", alignItems: "center" }}>
      <input
        type="checkbox"
        checked={allSelected}
        onChange={onSelectAll}
        style={{ width: "16px", height: "16px", cursor: "pointer" }}
      />
    </div>
    <div style={{ flex: 2 }}>Driver Name</div>
    <div style={{ flex: 1.5, textAlign: "center" }}>Vehicle Number</div>
    <div style={{ flex: 1.5, textAlign: "center" }}>Vehicle Type</div>
    <div style={{ width: "80px", textAlign: "center" }}>Actions</div>
  </div>
);

const DriverDropdown = ({ drivers, isInactive }) => {
  const driverNames = drivers.map((d) => d.driverName || d.name || "N/A");

  return (
    <span
      style={{
        color: isInactive ? "#9CA3AF" : "#333",
        textDecoration: isInactive ? "line-through" : "none",
      }}
    >
      {driverNames.join(", ")}
    </span>
  );
};

const TableRow = ({
  data,
  isSelected,
  onSelect,
  onViewDetails,
  onEdit,
  onToggleStatus,
  onAssign,
  onVehicleNumberClick,
  onViewHistory,
}) => {
  const hasClient =
    data.clientNames &&
    data.clientNames.length > 0 &&
    data.clientNames[0] !== "N/A";
  const isInactive = data.status?.toLowerCase() === "inactive";
  const baseBgColor = isInactive ? "#FEF2F2" : "white";
  const hoverBgColor = isInactive ? "#FEE2E2" : "#FAFAFA";

  // Get drivers array - either from drivers property or single driver
  const drivers =
    data.drivers?.length > 0
      ? data.drivers
      : data.displayDriverName || data.driverName || data.driver?.driverName
        ? [
            {
              driverName:
                data.displayDriverName ||
                data.driverName ||
                data.driver?.driverName ||
                data.driver?.name ||
                data.ownerName,
              contactNumber:
                data.displayDriverPhone || data.driver?.contactNumber,
            },
          ]
        : [];

  return (
    <div
      style={{
        display: "flex",
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0",
        alignItems: "center",
        fontSize: "14px",
        color: isInactive ? "#9CA3AF" : "#333",
        backgroundColor: baseBgColor,
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = hoverBgColor)
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = baseBgColor)
      }
    >
      <div style={{ width: "50px", display: "flex", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
        />
      </div>
      <div
        style={{ flex: 2, fontWeight: 400, cursor: "pointer" }}
        onClick={onViewDetails}
      >
        {drivers.length > 0 ? (
          <DriverDropdown drivers={drivers} isInactive={isInactive} />
        ) : (
          <span
            style={{
              color: isInactive ? "#9CA3AF" : "#333",
              textDecoration: isInactive ? "line-through" : "none",
            }}
          >
            {data.ownerName || (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails();
                }}
                style={{
                  backgroundColor: '#E8F5EE',
                  color: '#07A95B',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#D1E7DD';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#E8F5EE';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                View Profile
              </button>
            )}
          </span>
        )}
      </div>
      <div
        style={{
          flex: 1.5,
          textAlign: "center",
          fontFamily: "monospace",
          fontSize: "12px",
          color: isInactive ? "#9CA3AF" : "#20B967",
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationColor: isInactive ? "transparent" : "#20B967",
          textDecorationThickness: "1px",
          textUnderlineOffset: "2px",
        }}
        onClick={() => onVehicleNumberClick && onVehicleNumberClick(data)}
        onMouseEnter={(e) => {
          if (!isInactive) {
            e.currentTarget.style.color = "#1A8F55";
            e.currentTarget.style.textDecorationColor = "#1A8F55";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = isInactive ? "#9CA3AF" : "#20B967";
          e.currentTarget.style.textDecorationColor = isInactive
            ? "transparent"
            : "#20B967";
        }}
      >
        {data.vehicleNumber || data.registrationNumber || "N/A"}
      </div>
      <div
        style={{
          flex: 1.5,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {(() => {
          const vType = data.vehicleType || data.vehicleBrand || "Sedan";
          const badgeStyle = getVehicleTypeBadgeStyle(vType);
          return (
            <span
              style={{
                padding: "3px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                backgroundColor: badgeStyle.bg,
                color: badgeStyle.color,
              }}
            >
              {vType}
            </span>
          );
        })()}
        {isInactive && (
          <span
            style={{
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: "#FECACA",
              color: "#991B1B",
            }}
          >
            Inactive
          </span>
        )}
      </div>
      <div style={{ width: "80px", textAlign: "center" }}>
        <ActionsMenu
          onView={onViewDetails}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onAssign={onAssign}
          onViewHistory={onViewHistory}
          hasClient={hasClient}
          status={data.status}
        />
      </div>
    </div>
  );
};

const VehiclesManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [vehicleDetailsModalOpen, setVehicleDetailsModalOpen] = useState(false);
  const [selectedVehicleForDetails, setSelectedVehicleForDetails] =
    useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedVehicleForHistory, setSelectedVehicleForHistory] =
    useState(null);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [vehicleToAssign, setVehicleToAssign] = useState(null);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    clientName: "",
    vehicleType: "",
  });
  const [statusFilter, setStatusFilter] = useState("active"); // 'all', 'active', 'inactive'
  const [pagination, setPagination] = useState(() => {
    const pageFromParam = Number(searchParams.get("page"));
    const pageFromState = Number(location.state?.fromPage);
    const initialPage =
      pageFromParam > 0
        ? pageFromParam
        : pageFromState > 0
          ? pageFromState
          : 1;

    return {
      totalCount: 0,
      totalPages: 1,
      currentPage: initialPage,
      pageSize: 10,
    };
  });

  const handlePageChange = useCallback(
    (newPage) => {
      setPagination((prev) => {
        const targetPage = Math.max(1, Math.min(newPage, prev.totalPages || 1));
        if (prev.currentPage === targetPage) return prev;

        sessionStorage.setItem("vehicles_page", String(targetPage));

        setSearchParams(
          (prevParams) => {
            const next = new URLSearchParams(prevParams);
            if (targetPage > 1) {
              next.set("page", String(targetPage));
            } else {
              next.delete("page");
            }
            return next;
          },
          { replace: true }
        );

        return {
          ...prev,
          currentPage: targetPage,
        };
      });
    },
    [setSearchParams]
  );

  // Synchronize pagination state ONLY when URL searchParams change externally (e.g. browser back/forward buttons)
  useEffect(() => {
    const pageParam = Number(searchParams.get("page")) || 1;
    setPagination((prev) => {
      if (prev.currentPage !== pageParam) {
        sessionStorage.setItem("vehicles_page", String(pageParam));
        return {
          ...prev,
          currentPage: pageParam,
        };
      }
      return prev;
    });
  }, [searchParams]);

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    handlePageChange(1);
  };

  const handleVehicleNumberClick = async (vehicle) => {
    try {
      // Fetch complete vehicle details
      const vehicleResponse = await getVehicleById(vehicle.id);
      let vehicleData = vehicle;

      if (vehicleResponse.success && vehicleResponse.data) {
        vehicleData = vehicleResponse.data;
      }

      // Fetch account details separately
      try {
        const accountResponse = await getAccountDetails(vehicle.id);
        if (accountResponse.success && accountResponse.data) {
          const accountDetails = accountResponse.data; // single object from /by-vehicle/
          vehicleData = {
            ...vehicleData,
            accountDetailsId: accountDetails.id,
            accountHolderName: accountDetails.accountHolderName,
            accountNumber: accountDetails.accountNumber,
            bankName: accountDetails.bankName,
            bankCategory: accountDetails.bankCategory,
            ifscCode: accountDetails.ifscCode,
            branch: accountDetails.branch,
          };
        }
      } catch (accountErr) {
        // If account details fetch fails, continue with vehicle data only
      }

      setSelectedVehicleForDetails(vehicleData);
    } catch (err) {
      // Fallback to using vehicle data from table if API call fails
      setSelectedVehicleForDetails(vehicle);
    }
    setVehicleDetailsModalOpen(true);
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);

      // Fetch vehicles, assignments, drivers, and clients in parallel (single batch)
      const [vehiclesResp, assignmentsResp, driversResp, clientsResp] = await Promise.all([
        getAllVehicles(1, 1000).catch(() => ({ success: false, data: [] })),
        getAllVehicleClientAssignments(1, 1000).catch(() => ({ data: [] })),
        getAllDrivers().catch(() => ({ data: [] })),
        getAllClients(1, 1000).catch(() => ({ clients: [], data: [] }))
      ]);

      const allVehicles = vehiclesResp.data || [];
      const totalCount = vehiclesResp.totalCount || allVehicles.length;
      const totalPages = Math.ceil(totalCount / 10) || 1;

      const assignmentsResponse = { data: assignmentsResp.data || [] };
      const driversResponse = driversResp;
      const clientsResponse = clientsResp;

      const vehicles = allVehicles;

      setPagination((prev) => {
        const targetPage = prev.currentPage || 1;
        return {
          totalCount: totalCount || vehicles.length,
          totalPages: totalPages,
          currentPage: Math.max(1, Math.min(targetPage, totalPages)),
          pageSize: 10,
        };
      });

      // Get assignments data
      const assignments =
        assignmentsResponse.data || assignmentsResponse.assignments || [];

      // Get drivers data and create a map of vehicleId -> driverName
      // Handle multiple possible response structures
      const drivers =
        driversResponse.data ||
        driversResponse.drivers ||
        driversResponse ||
        [];
      const driverMap = {};

      if (Array.isArray(drivers)) {
        drivers.forEach((driver) => {
          const vehicleId =
            driver.vehicleId || driver.vehicle_id || driver.vehicle?.id;
          const driverName =
            driver.driverName || driver.driver_name || driver.name;
          const driverPhone =
            driver.contactNumber ||
            driver.contact_number ||
            driver.phone ||
            driver.mobile;
          if (vehicleId && driverName) {
            driverMap[vehicleId] = { name: driverName, phone: driverPhone };
          }
        });
      }

      // Create a map of clientId -> clientName for looking up client names from assignments
      const clients = clientsResponse?.clients || clientsResponse?.data || [];
      const clientMap = {};
      if (Array.isArray(clients)) {
        clients.forEach((client) => {
          const clientId = client.id;
          const clientName =
            client.clientName ||
            client.client_name ||
            client.companyName ||
            client.company_name ||
            client.name ||
            "Unknown";
          if (clientId) {
            clientMap[clientId] = clientName;
          }
        });

        // Populate clientsList for dropdown here to avoid extra API request
        const mappedClients = clients.map((c) => ({
          id: c.id,
          companyName:
            c.clientName ||
            c.client_name ||
            c.company_name ||
            c.companyName ||
            c.name ||
            "Unknown",
        }));
        setClientsList(mappedClients);
      }

      // Create a map of vehicleId -> clientName
      const vehicleClientMap = {};
      assignments.forEach((assignment) => {
        // Check all possible locations for vehicleId (lowercase and capitalized)
        const vehicleId =
          assignment.vehicleId ||
          assignment.vehicle_id ||
          assignment.vehicle?.id ||
          assignment.Vehicle?.id;
        // Get clientId from various possible locations
        const clientId =
          assignment.clientId ||
          assignment.client_id ||
          assignment.client?.id ||
          assignment.Client?.id;

        // Try to get clientName from embedded object first, then from clientMap lookup
        let clientName =
          assignment.client?.clientName ||
          assignment.client?.name ||
          assignment.client?.companyName ||
          assignment.Client?.clientName ||
          assignment.Client?.name ||
          assignment.Client?.companyName ||
          assignment.clientName ||
          assignment.client_name ||
          assignment.companyName ||
          assignment.client?.client_name ||
          assignment.Client?.client_name;

        // Fallback: lookup clientName from clientMap using clientId
        if (!clientName && clientId && clientMap[clientId]) {
          clientName = clientMap[clientId];
        }

        if (vehicleId && clientName) {
          vehicleClientMap[vehicleId] = clientName;
        }
      });

      // Also create a map by vehicleNumber/registrationNumber for fallback matching
      const vehicleClientMapByNumber = {};
      assignments.forEach((assignment) => {
        const vehicleNumber =
          assignment.vehicleNumber ||
          assignment.vehicle_number ||
          assignment.vehicle?.vehicleNumber ||
          assignment.vehicle?.registrationNumber ||
          assignment.Vehicle?.vehicleNumber ||
          assignment.Vehicle?.registrationNumber;
        const clientName =
          assignment.clientName ||
          assignment.client_name ||
          assignment.client?.clientName ||
          assignment.client?.name ||
          assignment.Client?.clientName ||
          assignment.Client?.name ||
          "Unknown";
        if (vehicleNumber) {
          vehicleClientMapByNumber[vehicleNumber.toUpperCase()] = clientName;
        }
      });

      // Create a map of vehicleId -> array of all drivers
      const vehicleDriversMap = {};
      if (Array.isArray(drivers)) {
        drivers.forEach((driver) => {
          const vehicleId =
            driver.vehicleId || driver.vehicle_id || driver.vehicle?.id;
          if (vehicleId) {
            if (!vehicleDriversMap[vehicleId]) {
              vehicleDriversMap[vehicleId] = [];
            }
            vehicleDriversMap[vehicleId].push({
              driverName:
                driver.driverName || driver.driver_name || driver.name,
              contactNumber:
                driver.contactNumber ||
                driver.contact_number ||
                driver.phone ||
                driver.mobile,
              licenseNumber: driver.licenseNumber || driver.license_number,
              id: driver.id,
            });
          }
        });
      }

      // Create a map of vehicleId -> array of all client names
      const vehicleClientsMap = {};
      assignments.forEach((assignment) => {
        const vehicleId =
          assignment.vehicleId ||
          assignment.vehicle_id ||
          assignment.vehicle?.id ||
          assignment.Vehicle?.id;
        const clientId =
          assignment.clientId ||
          assignment.client_id ||
          assignment.client?.id ||
          assignment.Client?.id;

        let clientName =
          assignment.client?.clientName ||
          assignment.client?.name ||
          assignment.client?.companyName ||
          assignment.Client?.clientName ||
          assignment.Client?.name ||
          assignment.Client?.companyName ||
          assignment.clientName ||
          assignment.client_name;

        // Fallback: lookup clientName from clientMap using clientId
        if (!clientName && clientId && clientMap[clientId]) {
          clientName = clientMap[clientId];
        }

        if (vehicleId && clientName) {
          if (!vehicleClientsMap[vehicleId]) {
            vehicleClientsMap[vehicleId] = [];
          }
          // Avoid duplicates
          if (!vehicleClientsMap[vehicleId].includes(clientName)) {
            vehicleClientsMap[vehicleId].push(clientName);
          }
        }
      });

      // Also match by vehicle number
      assignments.forEach((assignment) => {
        const vehicleNumber =
          assignment.vehicle?.vehicleNumber ||
          assignment.vehicle?.registrationNumber ||
          assignment.Vehicle?.vehicleNumber ||
          assignment.Vehicle?.registrationNumber;

        if (vehicleNumber) {
          const normalizedNumber = vehicleNumber.toUpperCase();
          const vehicle = vehicles.find(
            (v) =>
              (v.vehicleNumber || v.registrationNumber || "").toUpperCase() ===
              normalizedNumber,
          );

          if (vehicle) {
            const vehicleId = vehicle.id;
            let clientName =
              assignment.client?.clientName ||
              assignment.client?.name ||
              assignment.client?.companyName ||
              assignment.Client?.clientName ||
              assignment.Client?.name ||
              assignment.Client?.companyName ||
              assignment.clientName ||
              assignment.client_name;

            const clientId =
              assignment.clientId ||
              assignment.client_id ||
              assignment.client?.id ||
              assignment.Client?.id;
            if (!clientName && clientId && clientMap[clientId]) {
              clientName = clientMap[clientId];
            }

            if (clientName) {
              if (!vehicleClientsMap[vehicleId]) {
                vehicleClientsMap[vehicleId] = [];
              }
              if (!vehicleClientsMap[vehicleId].includes(clientName)) {
                vehicleClientsMap[vehicleId].push(clientName);
              }
            }
          }
        }
      });

      // Merge client names and driver names into vehicles
      const vehiclesWithDetails = vehicles.map((v) => {
        const vehicleId = v.id || v.vehicleId || v.vehicle_id;

        // Use drivers array from API if available, otherwise build from map
        let vehicleDrivers = v.drivers || [];

        // Fallback: check driver map from separate API call
        if (vehicleDrivers.length === 0 && vehicleDriversMap[vehicleId]) {
          vehicleDrivers = vehicleDriversMap[vehicleId];
        }

        // Fallback: single driver from vehicle object
        if (vehicleDrivers.length === 0 && (v.driver || v.driverName)) {
          vehicleDrivers = [
            {
              driverName:
                v.driver?.driverName ||
                v.driver?.name ||
                v.driverName ||
                v.driver_name ||
                v.ownerName,
              contactNumber:
                v.driver?.contactNumber ||
                v.driver?.contact_number ||
                v.displayDriverPhone,
            },
          ];
        }

        // Get all clients for this vehicle
        const vehicleClients = vehicleClientsMap[vehicleId] || [];

        // Fallback: check single client assignments
        let singleClient = vehicleClientMap[vehicleId];
        if (!singleClient) {
          const vehicleNumber = (
            v.vehicleNumber ||
            v.registrationNumber ||
            ""
          ).toUpperCase();
          if (vehicleNumber && vehicleClientMapByNumber[vehicleNumber]) {
            singleClient = vehicleClientMapByNumber[vehicleNumber];
          }
        }

        // Add single client if not already in list
        if (singleClient && !vehicleClients.includes(singleClient)) {
          vehicleClients.push(singleClient);
        }

        return {
          ...v,
          clientNames:
            vehicleClients.length > 0
              ? vehicleClients
              : v.clientName
                ? [v.clientName]
                : ["N/A"],
          clientName:
            vehicleClients[0] || v.clientName || v.client_name || "N/A",
          drivers: vehicleDrivers,
          displayDriverName:
            vehicleDrivers[0]?.driverName ||
            vehicleDrivers[0]?.driver_name ||
            v.driver?.driverName ||
            v.driverName ||
            v.ownerName ||
            "N/A",
          displayDriverPhone:
            vehicleDrivers[0]?.contactNumber ||
            vehicleDrivers[0]?.contact_number ||
            v.driver?.contactNumber ||
            "",
          status: v.status || "Active",
        };
      });

      setVehiclesData(vehiclesWithDetails);
    } catch (err) {
      setError("Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  };



  const handleAssignClick = async (vehicle) => {
    setVehicleToAssign(vehicle);

    // Fetch all assignments for this vehicle
    try {
      const assignmentsResponse = await getAllVehicleClientAssignments(1, 100);
      const assignments =
        assignmentsResponse.data || assignmentsResponse.assignments || [];
      const vehicleAllotments = assignments.filter(
        (a) =>
          (a.vehicleId || a.vehicle_id) === vehicle.id ||
          a.vehicle?.id === vehicle.id,
      );
      // Deduplicate by client ID
      const seenClientIds = new Set();
      const uniqueAssignments = vehicleAllotments.filter((a) => {
        const clientId = a.clientId || a.client_id || a.client?.id;
        if (seenClientIds.has(clientId)) {
          return false;
        }
        seenClientIds.add(clientId);
        return true;
      });
      setVehicleAssignments(uniqueAssignments);
      setSelectedClientId("");
    } catch (err) {
      setVehicleAssignments([]);
      setSelectedClientId("");
    }

    fetchClients();
    setAssignModalOpen(true);
  };

  const fetchClients = async () => {
    try {
      const response = await getAllClients(1, 100);
      const clientsArray = response.clients || response.data || [];

      if (clientsArray.length > 0) {
        const mappedClients = clientsArray.map((c) => ({
          id: c.id,
          companyName:
            c.clientName ||
            c.client_name ||
            c.company_name ||
            c.companyName ||
            c.name ||
            "Unknown",
        }));
        setClientsList(mappedClients);
      } else {
        setClientsList([]);
      }
    } catch (err) {
      toast.error("Failed to load clients: " + (err.message || "Unknown error"));
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedClientId || !vehicleToAssign) {
      toast.warning("Please select a client");
      return;
    }
    try {
      setAssigning(true);

      const assignmentsResponse = await getAllVehicleClientAssignments();
      const assignments = assignmentsResponse.data || [];

      // Check if already assigned to the same client
      const alreadyAssignedToThisClient = assignments.some(
        (a) =>
          (a.vehicleId || a.vehicle_id) === vehicleToAssign.id &&
          (a.clientId || a.client_id) === selectedClientId,
      );

      if (alreadyAssignedToThisClient) {
        toast.info("Vehicle is already assigned to this client");
        setAssignModalOpen(false);
        setVehicleToAssign(null);
        setSelectedClientId("");
        setAssigning(false);
        return;
      }

      // Create new assignment for this client
      const result = await assignVehicleToClient(
        selectedClientId,
        vehicleToAssign.id,
      );
      if (result.success) {
        toast.success("Vehicle assigned to client successfully!");
        fetchVehicles();
        
        // Refresh assignments for the modal
        const assignmentsResponse = await getAllVehicleClientAssignments(1, 100);
        const assignments = assignmentsResponse.data || assignmentsResponse.assignments || [];
        const vehicleAllotments = assignments.filter(
          (a) =>
            (a.vehicleId || a.vehicle_id) === vehicleToAssign.id ||
            a.vehicle?.id === vehicleToAssign.id,
        );
        setVehicleAssignments(vehicleAllotments);
        
        setAssignModalOpen(false);
        setVehicleToAssign(null);
        setSelectedClientId("");
      } else {
        toast.error(result.message || "Failed to assign vehicle");
      }
    } catch (err) {
      toast.error(
        "Error assigning vehicle: " +
          (err.response?.data?.message || err.message || "Unknown error"),
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleStatus = async (vehicle) => {
    const currentStatus = vehicle.status || "Active";
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    const isDeactivating = currentStatus === "Active";
    const actionText = isDeactivating ? "deactivate" : "activate";

    const confirmed = await showConfirm({
      title: isDeactivating ? "Deactivate Vehicle" : "Activate Vehicle",
      message: `Are you sure you want to ${actionText} this vehicle?`,
      danger: isDeactivating,
      type: isDeactivating ? "danger" : "success",
      confirmLabel: isDeactivating ? "Deactivate" : "Activate",
      cancelLabel: "Cancel",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await updateVehicle(vehicle.id, { status: newStatus });
      if (result.success) {
        toast.success(`Vehicle ${actionText}d successfully!`);
        // Update local state
        setVehiclesData((prev) =>
          prev.map((v) =>
            v.id === vehicle.id ? { ...v, status: newStatus } : v,
          ),
        );
      } else {
        toast.error(result.message || `Failed to ${actionText} vehicle`);
      }
    } catch (err) {
      toast.error(`Error ${actionText}ing vehicle: ` + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (vehicle) => {
    setVehicleToEdit(vehicle);
    setEditModalOpen(true);
    if (vehicle?.id) {
      try {
        const [vResp, accResp] = await Promise.allSettled([
          getVehicleById(vehicle.id),
          getAccountDetails(vehicle.id)
        ]);
        let enrichedVehicle = { ...vehicle };
        if (vResp.status === 'fulfilled' && vResp.value?.success && vResp.value?.data) {
          enrichedVehicle = { ...enrichedVehicle, ...vResp.value.data };
        }
        if (accResp.status === 'fulfilled' && accResp.value?.success && accResp.value?.data) {
          enrichedVehicle = { ...enrichedVehicle, accountDetails: accResp.value.data };
        }
        setVehicleToEdit(enrichedVehicle);
      } catch (e) {
        // Fallback: AddVehicleModal will independently fetch full details
      }
    }
  };

  const handleUnassignSpecific = async (assignmentId) => {
    const confirmed = await showConfirm({
      title: "Unassign Company",
      message: "Are you sure you want to unassign this company from the vehicle?",
      danger: true,
      confirmLabel: "Unassign",
      cancelLabel: "Cancel",
    });
    if (!confirmed) return;

    try {
      setAssigning(true);
      const result = await deleteVehicleClientAssignment(assignmentId);
      if (result.success) {
        toast.success("Company unassigned successfully!");
        // Refresh assignments for this vehicle
        const assignmentsResponse = await getAllVehicleClientAssignments(
          1,
          100,
        );
        const assignments =
          assignmentsResponse.data || assignmentsResponse.assignments || [];
        const vehicleAllotments = assignments.filter(
          (a) =>
            (a.vehicleId || a.vehicle_id) === vehicleToAssign.id ||
            a.vehicle?.id === vehicleToAssign.id,
        );
        setVehicleAssignments(vehicleAllotments);
        fetchVehicles();
      } else {
        toast.error(result.message || "Failed to unassign company");
      }
    } catch (err) {
      toast.error(
        "Error unassigning company: " +
          (err.response?.data?.message || err.message || "Unknown error"),
      );
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    const handleVehicleAdded = () => {
      fetchVehicles();
    };
    window.addEventListener('vehicleAdded', handleVehicleAdded);
    return () => {
      window.removeEventListener('vehicleAdded', handleVehicleAdded);
    };
  }, []);

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedIds.size === vehiclesData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(vehiclesData.map((v) => v.id)));
    }
  };

  const selectedVehicles = vehiclesData.filter((v) => selectedIds.has(v.id));
  const allSelected =
    vehiclesData.length > 0 && selectedIds.size === vehiclesData.length;

  // Apply search and filter logic
  const filteredVehicles = useMemo(() => {
    let result = [...vehiclesData];

    // Apply search term - vehicle number only
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter((v) => {
        const vehicleNo = (
          v.vehicleNumber ||
          v.registrationNumber ||
          ""
        ).toLowerCase();
        return vehicleNo.includes(searchLower);
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((v) => {
        const vehicleStatus = v.status || "Active";
        return statusFilter === "active"
          ? vehicleStatus.toLowerCase() === "active"
          : vehicleStatus.toLowerCase() !== "active";
      });
    }

    // Apply client name filter
    if (activeFilters.clientName) {
      result = result.filter((v) => v.clientName === activeFilters.clientName);
    }

    // Apply vehicle type filter
    if (activeFilters.vehicleType) {
      result = result.filter(
        (v) =>
          (v.vehicleType || v.vehicleBrand || "Sedan") ===
          activeFilters.vehicleType,
      );
    }

    return result;
  }, [vehiclesData, searchTerm, activeFilters, statusFilter]);

  // Paginate the filtered results
  const paginatedVehicles = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredVehicles.slice(startIndex, endIndex);
  }, [filteredVehicles, pagination.currentPage, pagination.pageSize]);

  // Update total pages when filtered results change
  useEffect(() => {
    if (filteredVehicles.length > 0) {
      const totalPages =
        Math.ceil(filteredVehicles.length / pagination.pageSize) || 1;
      setPagination((prev) => {
        const clampedPage =
          prev.currentPage > totalPages ? totalPages : prev.currentPage;
        if (prev.totalPages === totalPages && prev.currentPage === clampedPage) {
          return prev;
        }
        return {
          ...prev,
          totalPages,
          currentPage: clampedPage,
        };
      });
    }
  }, [filteredVehicles.length, pagination.pageSize]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", gap: "16px" }}>
        <StatCard
          value={vehiclesData.length.toString().padStart(3, "0")}
          label="Total Vehicles"
          isActive={statusFilter === "all"}
          onClick={() => {
            setStatusFilter("all");
            handlePageChange(1);
          }}
        />
        <StatCard
          value={vehiclesData
            .filter((v) => v.status?.toLowerCase() === "active")
            .length.toString()
            .padStart(3, "0")}
          label="Active Vehicles"
          isActive={statusFilter === "active"}
          onClick={() => {
            setStatusFilter("active");
            handlePageChange(1);
          }}
        />
        <StatCard
          value={vehiclesData
            .filter((v) => v.status?.toLowerCase() !== "active")
            .length.toString()
            .padStart(3, "0")}
          label="Inactive Vehicles"
          isActive={statusFilter === "inactive"}
          onClick={() => {
            setStatusFilter("inactive");
            handlePageChange(1);
          }}
        />
      </div>
      <ActionBar
        onAddClick={() => setAddModalOpen(true)}
        onUploadClick={() => setUploadModalOpen(true)}
        vehiclesData={vehiclesData}
        selectedVehicles={selectedVehicles}
        searchValue={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          handlePageChange(1);
        }}
        clientsList={clientsList}
        onApplyFilters={handleApplyFilters}
      />
      {error && (
        <div
          style={{
            color: "#F04438",
            padding: "16px",
            backgroundColor: "#FEF2F2",
            borderRadius: "8px",
            marginTop: "16px",
          }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          Loading vehicles...
        </div>
      ) : (
        <div
          style={{
            marginTop: "16px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
            overflow: "hidden",
          }}
        >
          <TableHeader
            allSelected={allSelected}
            onSelectAll={toggleAllSelection}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            {paginatedVehicles.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                {searchTerm
                  ? "No vehicles match your search"
                  : "No vehicles found"}
              </div>
            ) : (
              paginatedVehicles.map((vehicle) => (
                <TableRow
                  key={vehicle.id}
                  data={vehicle}
                  isSelected={selectedIds.has(vehicle.id)}
                  onSelect={() => toggleSelection(vehicle.id)}
                  onViewDetails={() =>
                    navigate(`/vehicles/${vehicle.id}`, {
                      state: { fromPage: pagination.currentPage },
                    })
                  }
                  onEdit={() => handleEditClick(vehicle)}
                  onToggleStatus={() => handleToggleStatus(vehicle)}
                  onAssign={() => handleAssignClick(vehicle)}
                  onVehicleNumberClick={handleVehicleNumberClick}
                  onViewHistory={() => {
                    setSelectedVehicleForHistory(vehicle);
                    setHistoryModalOpen(true);
                  }}
                />
              ))
            )}
          </div>
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "16px",
                gap: "8px",
                borderTop: "1px solid #F0F0F0",
              }}
            >
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  backgroundColor:
                    pagination.currentPage <= 1 ? "#f5f5f5" : "white",
                  color: pagination.currentPage <= 1 ? "#999" : "#333",
                  cursor:
                    pagination.currentPage <= 1 ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                Previous
              </button>
              {(() => {
                const current = pagination.currentPage;
                const total = pagination.totalPages;
                const pages = [];

                if (total <= 3) {
                  // Show all if 3 or fewer pages
                  for (let i = 1; i <= total; i++) {
                    pages.push(i);
                  }
                } else {
                  // Show limited pages with current in middle when possible
                  if (current === 1) {
                    pages.push(1, 2, 3);
                  } else if (current === total) {
                    pages.push(total - 2, total - 1, total);
                  } else {
                    pages.push(current - 1, current, current + 1);
                  }
                }

                return pages.map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      backgroundColor:
                        pagination.currentPage === page ? "#20B967" : "white",
                      color: pagination.currentPage === page ? "white" : "#333",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight:
                        pagination.currentPage === page ? "600" : "400",
                    }}
                  >
                    {page}
                  </button>
                ));
              })()}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  backgroundColor:
                    pagination.currentPage >= pagination.totalPages
                      ? "#f5f5f5"
                      : "white",
                  color:
                    pagination.currentPage >= pagination.totalPages
                      ? "#999"
                      : "#333",
                  cursor:
                    pagination.currentPage >= pagination.totalPages
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "14px",
                }}
              >
                Next
              </button>
              <span
                style={{ fontSize: "14px", color: "#666", marginLeft: "8px" }}
              >
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
            </div>
          )}
        </div>
      )}
      <AddVehicleModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={() => {
          fetchVehicles();
          setAddModalOpen(false);
        }}
      />
      <AddVehicleModal
        isOpen={editModalOpen}
        vehicle={vehicleToEdit}
        onClose={() => {
          setEditModalOpen(false);
          setVehicleToEdit(null);
        }}
        onSave={() => {
          fetchVehicles();
          setEditModalOpen(false);
          setVehicleToEdit(null);
        }}
      />
      {assignModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              width: "450px",
              maxWidth: "90%",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "18px",
                fontWeight: 600,
              }}
            >
              Assign Vehicle to Client
            </h3>
            <p
              style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}
            >
              Vehicle:{" "}
              <strong>
                {vehicleToAssign?.vehicleNumber ||
                  vehicleToAssign?.registrationNumber}
              </strong>
            </p>
            {vehicleAssignments.length > 0 && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: "#EEF8F3",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#1F2937",
                }}
              >
                <strong>Currently assigned to:</strong>
                <ul
                  style={{
                    margin: "8px 0 0 0",
                    paddingLeft: "20px",
                    listStyle: "none",
                  }}
                >
                  {vehicleAssignments.map((assignment, idx) => (
                    <li
                      key={idx}
                      style={{
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {assignment.client?.clientName ||
                          assignment.client?.companyName ||
                          assignment.clientName ||
                          "Unknown Client"}
                      </span>
                      <button
                        onClick={() =>
                          handleUnassignSpecific(
                            assignment.id || assignment.assignment_id,
                          )
                        }
                        disabled={assigning}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: assigning ? "not-allowed" : "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          color: "#EF4444",
                          opacity: assigning ? 0.5 : 1,
                        }}
                        title="Unassign company"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Select Client to Add
              </label>
              <CustomSelect
                value={selectedClientId}
                onChange={(val) => setSelectedClientId(val)}
                options={[
                  { value: "", label: "-- Select a client --" },
                  ...clientsList.map((client) => {
                    const isAlreadyAssigned = vehicleAssignments.some(
                      (a) => (a.clientId || a.client_id) === client.id,
                    );
                    return {
                      value: client.id,
                      label: `${client.companyName || client.name} ${isAlreadyAssigned ? "(Already assigned)" : ""}`,
                      disabled: isAlreadyAssigned,
                    };
                  }),
                ]}
                placeholder="-- Select a client --"
                searchable
                width="100%"
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setAssignModalOpen(false);
                  setVehicleToAssign(null);
                  setSelectedClientId("");
                  setVehicleAssignments([]);
                }}
                style={{
                  padding: "10px 16px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                disabled={assigning || !selectedClientId}
                style={{
                  padding: "10px 16px",
                  border: "none",
                  backgroundColor: "#20B967",
                  color: "white",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  opacity: assigning || !selectedClientId ? 0.6 : 1,
                }}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}
      {uploadModalOpen && (
        <UploadSheetModal
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUpload={async (file) => {
            try {
              const response = await uploadVehicleSheet(file);

              // Build detailed summary message
              const summary = response.summary;
              let message = "Vehicle sheet uploaded successfully!\n\n";
              message += `Total rows: ${summary.total}\n`;
              message += `Unique vehicles: ${summary.uniqueVehicles}\n`;
              message += `Created: ${summary.created}\n`;

              if (summary.skipped > 0) {
                message += `Skipped (duplicates): ${summary.skipped}\n`;
              }

              if (summary.failed > 0) {
                message += `Failed: ${summary.failed}\n`;
              }

              // Show details of skipped vehicles if any
              if (response.results?.skipped?.length > 0) {
                message += "\nSkipped vehicles:\n";
                response.results.skipped.slice(0, 5).forEach((skip, idx) => {
                  message += `- ${skip.skippedRow}: ${skip.reason}\n`;
                });
                if (response.results.skipped.length > 5) {
                  message += `... and ${response.results.skipped.length - 5} more\n`;
                }
              }

              // Show errors if any
              if (response.results?.errors?.length > 0) {
                message += "\nErrors (first 5):\n";
                response.results.errors.slice(0, 5).forEach((err, idx) => {
                  message += `- ${err.vehicleKey}: ${err.error}\n`;
                });
                if (response.results.errors.length > 5) {
                  message += `... and ${response.results.errors.length - 5} more errors\n`;
                }
                toast.warning(message);
              } else {
                toast.success(message);
              }

              fetchVehicles();
              setUploadModalOpen(false);
            } catch (err) {
              toast.error(
                "Failed to upload sheet: " +
                  (err.response?.data?.message || err.message),
              );
            }
          }}
        />
      )}

      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        isOpen={vehicleDetailsModalOpen}
        onClose={() => {
          setVehicleDetailsModalOpen(false);
          setSelectedVehicleForDetails(null);
        }}
        vehicle={selectedVehicleForDetails}
      />

      {/* Vehicle History Modal */}
      <VehicleHistoryModal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedVehicleForHistory(null);
        }}
        vehicle={selectedVehicleForHistory}
      />
    </div>
  );
};

export default VehiclesManagement;
